import { useAuth } from '../../contexts/AuthContext';
import { useSettingsQuery } from '../../hooks/useSettingsQuery';
import { useUpdateSettings } from '../../hooks/useUpdateSettings';
import { BlockedUsersSection } from './BlockedUsersSection';
import { Skeleton } from '../ui/Skeleton';

export function PrivacySection() {
  const { profile } = useAuth();
  const { data: settings, isLoading } = useSettingsQuery();
  const updateSettings = useUpdateSettings();

  // Full skeleton only when BOTH profile and query data are unavailable
  if (isLoading && !settings && !profile) {
    return (
      <div className="bg-[#1A1F26] rounded-xl p-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Query data wins when available, profile is instant fallback
  const visibility = settings?.visibility ?? profile?.visibility ?? 'public';
  const activityEnabled = settings?.activity_visibility_enabled ?? profile?.activity_visibility_enabled ?? true;
  const allowRecommendations = settings?.allow_recommendations ?? profile?.allow_recommendations ?? 'followers';
  const showInSearch = settings?.show_in_search ?? profile?.show_in_search ?? true;

  const handleVisibilityChange = (value: 'public' | 'friends' | 'private') => {
    updateSettings.mutate({ visibility: value });
  };

  const handleActivityToggle = (enabled: boolean) => {
    updateSettings.mutate({ activity_visibility_enabled: enabled });
  };

  const handleRecommendationsChange = (value: 'followers' | 'none') => {
    updateSettings.mutate({ allow_recommendations: value });
  };

  const handleShowInSearchToggle = (enabled: boolean) => {
    updateSettings.mutate({ show_in_search: enabled });
  };

  return (
    <div className="bg-[#1A1F26] rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4">PRIVACY</h2>
      <div className="space-y-4">
        {/* Profile Visibility */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-white font-medium">Profile Visibility</div>
              <div className="text-sm text-slate-400">Who can see your full profile</div>
            </div>
            <select
              value={visibility}
              onChange={(e) => handleVisibilityChange(e.target.value as 'public' | 'friends' | 'private')}
              className="px-3 py-1.5 bg-[#2A2F36] text-white rounded-lg border border-white/[0.08] focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={updateSettings.isPending}
            >
              <option value="public">Public</option>
              <option value="friends">Friends</option>
              <option value="private">Private</option>
            </select>
          </div>
        </div>

        {/* Activity Visibility */}
        <div className="pt-4 border-t border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">Show Activity to Friends</div>
              <div className="text-sm text-slate-400">Let mutual followers see your activity feed</div>
            </div>
            <button
              onClick={() => handleActivityToggle(!activityEnabled)}
              disabled={updateSettings.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                activityEnabled ? 'bg-teal-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  activityEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Allow Recommendations */}
        <div className="pt-4 border-t border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-white font-medium">Allow Recommendations</div>
              <div className="text-sm text-slate-400">Who can send you movie recommendations</div>
            </div>
            <select
              value={allowRecommendations}
              onChange={(e) => handleRecommendationsChange(e.target.value as 'followers' | 'none')}
              className="px-3 py-1.5 bg-[#2A2F36] text-white rounded-lg border border-white/[0.08] focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={updateSettings.isPending}
            >
              <option value="followers">Followers</option>
              <option value="none">No one</option>
            </select>
          </div>
        </div>

        {/* Appear in Search */}
        <div className="pt-4 border-t border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">Appear in Search</div>
              <div className="text-sm text-slate-400">Let others find you via search</div>
            </div>
            <button
              onClick={() => handleShowInSearchToggle(!showInSearch)}
              disabled={updateSettings.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showInSearch ? 'bg-teal-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showInSearch ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Blocked Users */}
        <BlockedUsersSection />
      </div>
    </div>
  );
}
